import React, { useState } from 'react';
import {
  FiUsers,
  FiDollarSign,
  FiStar,
  FiActivity,
  FiLoader,
  FiShield,
} from 'react-icons/fi';
import MemberGrowthChart from './MemberGrowthChart';
import MemberAcquisition from './MemberAcquisition';
import LoanPerformance from './LoanPerformance';
import UnderservedMarket from './UnderservedMarket';
import FinancialHealth from './FinancialHealth';
import LiveMetricCard from './LiveMetricCard';
import DataSourceBadge from './DataSourceBadge';
import DataSourceNotConnected from './DataSourceNotConnected';
import DataSufficiencyWrapper from './DataSufficiencyWrapper';
import InsightsErrorBoundary from '../../common/InsightsErrorBoundary';
import ExecutiveSummaryPanel from '../common/ExecutiveSummaryPanel';
import { useInsightsData } from '../../../hooks/useInsightsData';
import { NOT_CONNECTED_REGISTRY } from '../../../types/insightsTypes';

const DashboardOverview: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { data, loading, error } = useInsightsData();

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'member-acquisition', label: 'Member Acquisition' },
    { id: 'loan-performance', label: 'Loan Performance' },
    { id: 'underserved-market', label: 'Underserved Market' },
    { id: 'financial-health', label: 'Financial Health' }
  ];

  const renderOverviewContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <FiLoader className="animate-spin text-slate-400 mr-3" size={20} />
          <span className="text-slate-500">Loading overview data…</span>
        </div>
      );
    }
    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-medium">Failed to load insights data</p>
          <p className="text-red-500 text-sm mt-1">{error.message}</p>
        </div>
      );
    }
    const ov = data?.overview;
    if (!ov) return null;

    return (
      <div className="space-y-6">
        {/* Executive Summary */}
        <ExecutiveSummaryPanel tab="overview" overview={ov} />

        {/* Date Range Label */}
        {ov.date_range.label && (
          <p className="text-xs text-slate-400 font-medium">{ov.date_range.label}</p>
        )}

        {/* Portfolio Health Index — Anchor KPI */}
        {ov.portfolio_health_index.status !== 'not_connected' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <LiveMetricCard
              title="Portfolio Health Index"
              metric={ov.portfolio_health_index}
              icon={<FiShield className="text-slate-600" size={20} />}
              format="score"
              target="70"
            />
          </div>
        )}

        {/* Key Metrics Grid */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-base sm:text-lg font-medium text-slate-700">
              High-level performance metrics across all key areas
            </h3>
            <DataSourceBadge status="live" source="lender_insights_overview_v" />
          </div>
          <DataSufficiencyWrapper
            sufficiency={ov.total_members.sufficiency}
            sampleSize={ov.total_members.sample_size}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <LiveMetricCard
                title="Total Members"
                metric={ov.total_members}
                icon={<FiUsers className="text-slate-600" size={20} />}
                format="number"
                target="15,000"
                metricKey="overview.total_members"
              />
              <LiveMetricCard
                title="Avg VeraScore"
                metric={ov.avg_verascore}
                icon={<FiActivity className="text-slate-600" size={20} />}
                format="score"
                target="70"
                metricKey="financial_health.avg_verascore"
              />
              <LiveMetricCard
                title="Revenue per Member"
                metric={ov.revenue_per_member}
                icon={<FiDollarSign className="text-slate-600" size={20} />}
                format="currency"
                requiredIntegration="core_banking"
                metricKey="overview.revenue_per_member"
              />
              <LiveMetricCard
                title="Member Satisfaction"
                metric={ov.member_satisfaction}
                icon={<FiStar className="text-slate-600" size={20} />}
                format="ratio"
                requiredIntegration="survey"
                metricKey="overview.member_satisfaction"
              />
            </div>
          </DataSufficiencyWrapper>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Member Growth Chart (PARTIAL — count only, no revenue axis) */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base sm:text-lg font-medium text-slate-700">Member Growth</h4>
              <DataSourceBadge
                status={ov.member_growth_count.status}
                source="lender_acquisition_v"
                compact
              />
            </div>
            <div className="h-64 sm:h-80">
              <MemberGrowthChart series={ov.member_growth_count} />
            </div>
          </div>

          {/* Loan Application Funnel — NOT_CONNECTED (LOS) */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
            <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Loan Application Funnel</h4>
            <DataSourceNotConnected
              info={NOT_CONNECTED_REGISTRY.los}
              metricName="Loan Application Funnel"
            />
          </div>
        </div>

        {/* Loan Portfolio Distribution — NOT_CONNECTED (LOS) */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Loan Portfolio Distribution</h4>
          <DataSourceNotConnected
            info={NOT_CONNECTED_REGISTRY.los}
            metricName="Loan Portfolio Distribution"
          />
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewContent();
      case 'member-acquisition':
        return <InsightsErrorBoundary section="Member Acquisition"><MemberAcquisition /></InsightsErrorBoundary>;
      case 'loan-performance':
        return <InsightsErrorBoundary section="Loan Performance"><LoanPerformance /></InsightsErrorBoundary>;
      case 'underserved-market':
        return <InsightsErrorBoundary section="Underserved Market"><UnderservedMarket /></InsightsErrorBoundary>;
      case 'financial-health':
        return <InsightsErrorBoundary section="Financial Health"><FinancialHealth /></InsightsErrorBoundary>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex overflow-x-auto scrollbar-hide space-x-2 sm:space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-3 sm:px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'border-slate-600 text-slate-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default DashboardOverview;